import { call, Effect, ForkEffect, put, takeEvery } from 'redux-saga/effects';
import { profileDataActions } from './slice';
import { errorMessages } from '../../containers/common/utils/constants';
import axiosInstance from '../../containers/common/utils/axios-service';
import { AxiosResponse } from 'axios';
import { IProfileData } from '../../containers/common/utils/interfaces';
import { PayloadAction } from '@reduxjs/toolkit';
import { errorToast, successToast } from '../../containers/common/utils/alert';
import { getDisplayMessage } from '../../containers/common/utils/error-handler';
import { getSignedInEmail } from '../../containers/common/utils/cookie-service';

const getProfileDataAsync: (email: string) => Promise<AxiosResponse> = async (
  email: string
): Promise<AxiosResponse> => {
  return await axiosInstance.get(`getCurrentUser?email=${email}`);
};

export const getProfilePicAsync: (
  userId: string
) => Promise<AxiosResponse> = async (
  userId: string
): Promise<AxiosResponse> => {
  return await axiosInstance.get(`fetchProfilePicture?userId=${userId}`);
};

export function arrayBufferToDataURL(
  arrayBuffer: ArrayBuffer,
  mimeType: string
): string {
  const base64String = btoa(
    String.fromCharCode.apply(
      null,
      new Uint8Array(arrayBuffer) as unknown as number[]
    )
  );
  return `data:${mimeType};base64,${base64String}`;
}

export function* watchGetProfileDataAsync(): Generator<Effect, void> {
  try {
    const email = getSignedInEmail();
    const profileDataRes = (yield call(
      getProfileDataAsync,
      email
    )) as AxiosResponse;

    let profilePicRes: AxiosResponse | null = null;
    try {
      profilePicRes = (yield call(
        getProfilePicAsync,
        profileDataRes.data.userId
      )) as AxiosResponse;
    } catch (error) {
      console.error(error);
    }

    // Example usage:
    const mimeType = 'image/jpg'; // Specify the MIME type accordingly
    const dataURL = arrayBufferToDataURL(
      profilePicRes?.data.profilePic.data,
      mimeType
    );

    if (profileDataRes?.data) {
      if (profilePicRes?.data.profilePic) {
        profileDataRes.data.profilePic = dataURL;
      }
      yield put(profileDataActions.getProfileDataSuccess(profileDataRes.data));
    } else {
      yield put(
        profileDataActions.getProfileDataFail(
          errorMessages.ErrorMessageInvalidDataFormat
        )
      );
    }
  } catch (error) {
    console.error(error);
    yield put(
      profileDataActions.getProfileDataFail(
        errorMessages.ErrorMessageGettingProfileData
      )
    );
  }
}

async function updatePhoneNumberAsync(
  profileData: IProfileData
): Promise<AxiosResponse> {
  return await axiosInstance.post('manage-employee', {
    operationName: 'updateSignedInUser',
    query: `
      mutation updateSignedInUser($employee: UpdateEmployeeSignedInDto!) {
          updateEmployeeSignedIn(employee: $employee) {
          mobilePhone
        }
      }
      `,
    variables: {
      employee: {
        mobilePhone: profileData.mobilePhone
      }
    }
  });
}

export function* watchUpdatePhoneNumberAsync({
  payload
}: PayloadAction<IProfileData>): Generator<Effect, void> {
  try {
    const updateResponse = (yield call(
      updatePhoneNumberAsync,
      payload
    )) as AxiosResponse;
    if (updateResponse?.data?.data?.updateEmployeeSignedIn?.mobilePhone) {
      yield put(profileDataActions.updatePhoneNumberSuccess(payload));
    } else {
      console.error(updateResponse.data.errors);
      if (
        updateResponse &&
        typeof updateResponse === 'object' &&
        updateResponse.data &&
        updateResponse.data.errors
      ) {
        const displayMsg = getDisplayMessage(updateResponse.data.errors);
        if (displayMsg) {
          errorToast(displayMsg);
          yield put(profileDataActions.updatePhoneNumberFailed(displayMsg));
        }
      }
    }
  } catch (error) {
    console.error(error);
    yield put(
      profileDataActions.updatePhoneNumberFailed(
        errorMessages.ErrorMessageCommon
      )
    );
    errorToast(errorMessages.ErrorMessageCommon);
  }
}

async function updateProfilePictureAsync(
  image: string
): Promise<AxiosResponse> {
  const res = await axiosInstance.post('manage-employee', {
    operationName: 'updateSignedInPhoto',
    query: `
      mutation updateSignedInPhoto($employee: UpdatePhotoSignedInDto!) {
          updatePhotoSignedIn(employee: $employee) {
          profilePic
        }
      }
      `,
    variables: {
      employee: {
        contentType: 'image/jpeg',
        photo: image
      }
    }
  });
  return res;
}

export function* watchUpdateProfilePictureAsync({
  payload
}: PayloadAction<string>): Generator<Effect, void> {
  try {
    const updateResponse = (yield call(
      updateProfilePictureAsync,
      payload
    )) as AxiosResponse;
    if (updateResponse?.data?.data?.updatePhotoSignedIn) {
      yield put(profileDataActions.updateProfilePictureSuccess(payload));
      yield put(profileDataActions.getProfileData());
      successToast(
        'Profile Picture updated successfully. Loading new Image may take few seconds.'
      );
      // window.location.reload();
    } else {
      console.error(updateResponse.data.errors);
      if (
        updateResponse &&
        typeof updateResponse === 'object' &&
        updateResponse.data &&
        updateResponse.data.errors
      ) {
        const displayMsg = getDisplayMessage(updateResponse.data.errors);
        if (displayMsg) {
          errorToast(displayMsg);
        }
      }
    }
  } catch (error) {
    console.error(error);
    yield put(
      profileDataActions.updateProfilePictureFailed(
        errorMessages.ErrorMessageCommon
      )
    );
    errorToast(errorMessages.ErrorMessageCommon);
  }
}

export function* watchProfileDataSagas(): Generator<ForkEffect, void> {
  yield takeEvery(profileDataActions.getProfileData, watchGetProfileDataAsync);
  yield takeEvery(
    profileDataActions.updatePhoneNumberStart,
    watchUpdatePhoneNumberAsync
  );
  yield takeEvery(
    profileDataActions.updateProfilePictureStart,
    watchUpdateProfilePictureAsync
  );
}

const profileDataSagas = watchProfileDataSagas;

export default profileDataSagas;
